// Quiz mapping and resolver utilities with Excel fallback
import authService from './authService';
import { buildApiActionUrl } from '../utils/apiConfig';
// Use require() to avoid needing @types/xlsx in the repo. XLSX will be typed as `any`.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX: any = require('xlsx');

let cachedList: string[] | null = null;

const knownMapping: Record<string, string[]> = {
  // Android identifiers -> possible server set names (common variants)
  'M3_U1_X99': ['M3_U1_X99', 'quiz_3_1', 'M3_U1'],
  'M3_U2_X99': ['M3_U2_X99', 'quiz_3_2', 'M3_U2'],
  // Add more mappings as we extract them from Android sources
};

async function getQuizList(): Promise<string[]> {
  if (cachedList) return cachedList;
  try {
    const token = authService.getToken();
    const res = await fetch(buildApiActionUrl('getQuizList'), { headers: token ? { Authorization: `Bearer ${token}` } as any : {} as any });
    if (!res.ok) throw new Error('Fehler beim Laden der Quiz-Liste');
    const data = await res.json();
    const list: string[] = Array.isArray(data) ? data : data.sets || [];
    cachedList = list;
    return list;
  } catch (e) {
    cachedList = [];
    return [];
  }
}

async function checkSetHasQuestions(setName: string): Promise<boolean> {
  try {
    const token = authService.getToken();
    const res = await fetch(buildApiActionUrl('getQuizQuestions', { set: setName }), { headers: token ? { Authorization: `Bearer ${token}` } as any : {} as any });
    if (!res.ok) return false;
    const data = await res.json();
    const arr = Array.isArray(data) ? data : data.questions || data;
    return Array.isArray(arr) && arr.length > 0;
  } catch (e) {
    return false;
  }
}

function genCandidates(unitId: string): string[] {
  const c: string[] = [];
  if (!unitId) return c;
  c.push(unitId);
  if (unitId.endsWith('_X99')) c.push(unitId.replace('_X99', ''));
  if (unitId.includes('_')) c.push(unitId.replace(/_/g, '-'));
  c.push(unitId.toLowerCase());
  return Array.from(new Set(c));
}

export async function resolveQuizSetForUnit(unitId: string): Promise<string | null> {
  const list = await getQuizList();

  // 1) mapping table
  const mapped = knownMapping[unitId];
  if (mapped) {
    for (const m of mapped) {
      if (list.includes(m)) return m;
    }
  }

  // 2) direct contains / exact match
  for (const s of list) {
    if (s === unitId || s.includes(unitId) || s.toLowerCase() === unitId.toLowerCase()) return s;
  }

  // 3) generate candidates and test exact/case-insensitive
  const candidates = genCandidates(unitId);
  for (const cand of candidates) {
    const found = list.find(s => s === cand || s.toLowerCase() === cand.toLowerCase() || s.includes(cand));
    if (found) return found;
  }

  // 4) dynamic check: try candidate names (mapping + candidates) via getQuizQuestions
  const tryList = (mapped || []).concat(candidates).concat(list.slice(0, 20)); // limit checks
  for (const cand of tryList) {
    if (await checkSetHasQuestions(cand)) return cand;
  }

  return null;
}

// --- Excel parsing fallback -------------------------------------------------

type NormalizedQuestion = {
  id: string;
  question: string;
  options: string[];
  correctIndex: number; // -1 if unknown
  correctAnswer?: string; // ursprünglicher Text aus Spalte G (Excel Schema) falls verfügbar
};

function findKey(obj: any, re: RegExp): string | undefined {
  return Object.keys(obj).find(k => re.test(k));
}

function normalizeRowToQuestion(row: any, index: number): NormalizedQuestion | null {
  // heuristics to find question text
  const qKey = findKey(row, /question|frage|q\b|text/i) || Object.keys(row).find(k => typeof row[k] === 'string' && row[k].trim().length > 0);
  if (!qKey) return null;
  const question = String(row[qKey] ?? '').trim();
  if (!question) return null;

  // Collect option columns (A,B,C,D or Option1..)
  const optionKeys = Object.keys(row).filter(k => /^(A|B|C|D)$/.test(k.toUpperCase()) || /option|antwort|choice|answer_/i.test(k));
  // If no obvious option keys, take other string columns except question & correct
  if (optionKeys.length === 0) {
    const ex = [qKey];
    const corrKeyTest = findKey(row, /correct|answer|antwort|richtig|solution|correct_index|corr/i);
    if (corrKeyTest) ex.push(corrKeyTest);
    const others = Object.keys(row).filter(k => !ex.includes(k) && typeof row[k] === 'string');
    optionKeys.push(...others.slice(0, 6));
  }

  const options = optionKeys.map(k => String(row[k] ?? '').trim()).filter(s => s.length > 0);
  // Dedupliziere Optionen (falls korrekte Antwort doppelt vorkommt)
  const uniqueOptions: string[] = [];
  const seenLower = new Set<string>();
  for (const opt of options) {
    const lowerOpt = opt.toLowerCase();
    if (!seenLower.has(lowerOpt)) {
      seenLower.add(lowerOpt);
      uniqueOptions.push(opt);
    }
  }
  const finalOptions = uniqueOptions;

  // find correct answer
  const corrKey = findKey(row, /correct|answer|antwort|richtig|solution|correct_index|corr/i);
  let correctIndex = -1;
  if (corrKey) {
    const val = String(row[corrKey] ?? '').trim();
    if (/^[A-D]$/i.test(val)) {
      const letter = val.toUpperCase();
      correctIndex = 'ABCD'.indexOf(letter);
    } else if (/^\d+$/.test(val)) {
      const n = parseInt(val, 10);
      if (!isNaN(n)) correctIndex = Math.max(0, n - 1);
    } else if (val.length > 0) {
      // try to match option text
      const found = finalOptions.findIndex(o => o.toLowerCase() === val.toLowerCase());
      if (found >= 0) correctIndex = found;
    }
  }

  // If no correctIndex and options length 1, assume 0
  if (correctIndex === -1 && finalOptions.length === 1) correctIndex = 0;

  return {
    id: `excel_${index}`,
    question,
    options: finalOptions,
    correctIndex,
  };
}

export async function loadQuestionsFromExcel(sheetPrefix: string): Promise<NormalizedQuestion[]> {
  try {
    const res = await fetch('/assets/Quiz.xlsx');
    if (!res.ok) return [];
    const ab = await res.arrayBuffer();
    const wb = XLSX.read(ab, { type: 'array' });
    let sheetNames = wb.SheetNames.filter((name: string) => name.startsWith(sheetPrefix) || name.toLowerCase().includes(sheetPrefix.toLowerCase()));

    if (sheetNames.length === 0) {
      const alt = sheetPrefix.replace(/-/g, '_');
      sheetNames = wb.SheetNames.filter((name: string) => name.startsWith(alt) || name.toLowerCase().includes(alt.toLowerCase()));
    }

    // Helper: String normalisieren (wie quizPageFactory)
    const toTrimmedString = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'number' && Number.isFinite(value)) {
        return Number.isInteger(value) ? value.toString() : value.toString();
      }
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      return value.toString().trim();
    };

    // Helper: Case-insensitive Vergleich (wie quizPageFactory)
    const equalsIgnoreCase = (a: string, b: string): boolean =>
      a.localeCompare(b, undefined, { sensitivity: 'accent', usage: 'search' }) === 0;

    // Android-Style Parser: Spalten B=Frage, C-F=Optionen, G=Korrekte Antwort (wie quizPageFactory)
    const parseFixedSchema = (sheet: any, sheetName: string): NormalizedQuestion[] => {
      const matrix: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      const result: NormalizedQuestion[] = [];
      if (!Array.isArray(matrix) || matrix.length < 2) return result;
      
      // Erste Zeile Header? (optional)
      const firstRowIsHeader = matrix[0]?.some((cell: any) => {
        const val = toTrimmedString(cell).toLowerCase();
        return val.includes('frage') || val.includes('option') || val === 'a' || val === 'b';
      });
      const startRow = firstRowIsHeader ? 1 : 0;

      for (let r = startRow; r < matrix.length; r++) {
        const row = matrix[r];
        if (!Array.isArray(row)) continue;

        // Spalte B (Index 1) = Frage
        const question = toTrimmedString(row[1]);
        if (!question) continue;

        // Spalten C-F (Index 2-5) = Optionen
        const options: string[] = [];
        for (let col = 2; col <= 5; col++) {
          const optText = toTrimmedString(row[col]);
          if (optText) options.push(optText);
        }
        if (options.length < 2) continue;

        // Spalte G (Index 6) = Korrekte Antwort (Text aus C-F)
        const correctText = toTrimmedString(row[6]);
        if (!correctText) continue;

        // Finde Index via equalsIgnoreCase (exakt wie quizPageFactory)
        let correctIndex = options.findIndex(opt => equalsIgnoreCase(opt, correctText));
        
        if (correctIndex === -1) {
          console.warn(`[Excel] Zeile ${r}: korrekte Antwort "${correctText}" nicht in Optionen gefunden.`);
          console.warn(`[Excel] Zeile ${r}: Spalte G = "${correctText}"`);
          console.warn(`[Excel] Zeile ${r}: Optionen C-F = [${options.map(o => `"${o}"`).join(', ')}]`);
          continue;
        }

        console.info(`[Excel] Zeile ${r}: Korrekte Antwort = Index ${correctIndex} (${options[correctIndex]})`);
        result.push({
          id: `excel_${r}`,
          question,
          options: options.slice(0, 4),
          correctIndex,
          correctAnswer: options[correctIndex]
        });
      }

      if (result.length === 0) {
        console.warn(`[Excel] Sheet "${sheetName}": Keine gültigen Fragen gefunden (Schema: B=Frage, C-F=Optionen, G=Korrekte als Text).`);
      } else {
        console.info(`[Excel] Sheet "${sheetName}": ${result.length} Fragen geladen.`);
      }
      return result;
    };

    const questions: NormalizedQuestion[] = [];
    for (const sheetName of sheetNames) {
      const sheet = wb.Sheets[sheetName];
      if (!sheet) continue;
      // Erst festes Schema versuchen
      const fixed = parseFixedSchema(sheet, sheetName);
      if (fixed.length > 0) {
        questions.push(...fixed);
        break; // erstes passendes Blatt verwenden
      }
      // Fallback: generisches Heuristik-Parsen
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      for (let i = 0; i < rows.length; i++) {
        const q = normalizeRowToQuestion(rows[i], i);
        if (q && q.options.length >= 2) questions.push(q);
      }
      if (questions.length > 0) break;
    }

    return questions;
  } catch (e) {
    console.warn('Excel load failed', e);
    return [];
  }
}

export async function loadQuestionsForSetName(setName: string): Promise<NormalizedQuestion[]> {
  // Try server first
  try {
    const token = authService.getToken();
    const res = await fetch(buildApiActionUrl('getQuizQuestions', { set: setName }), { headers: token ? { Authorization: `Bearer ${token}` } as any : {} as any });
    if (res.ok) {
      const data = await res.json();
      const arr = Array.isArray(data) ? data : data.questions || data;
      if (Array.isArray(arr) && arr.length > 0) {
        // Try to normalize server objects to NormalizedQuestion shape when possible
        const normalized = arr.map((it: any, idx: number) => {
          const rawOptions = it.options || it.answers || it.answers_list || [];
          const options: string[] = Array.isArray(rawOptions)
            ? rawOptions.map((o: any) => (typeof o === 'string' ? o : (o?.text ?? o?.answer ?? '')).toString()).filter(Boolean)
            : Object.values(rawOptions || {}).map(String);
          const correctText: string = (typeof it.correctText === 'string' ? it.correctText : '')
            || (Array.isArray(rawOptions) ? (rawOptions.find((o: any) => o?.correct === true)?.text || rawOptions.find((o: any) => o?.correct === true)?.answer || '') : '');
          const lowerCorrect = correctText ? correctText.trim().toLowerCase() : '';
          let correctIndex = -1;
          if (lowerCorrect) {
            correctIndex = options.findIndex(o => o.trim().toLowerCase() === lowerCorrect);
          }
          if (correctIndex < 0 && typeof it.correctIndex === 'number') correctIndex = it.correctIndex;
          if (correctIndex < 0) {
            console.warn(`[Server] Frage ${idx + 1} in Set "${setName}": Kein gültiger correctIndex bestimmt. Frage könnte problematisch sein.`);
          }
          return {
            id: it.id || `srv_${idx}`,
            question: it.question || it.text || it.q || '',
            options,
            correctIndex,
            correctAnswer: lowerCorrect ? correctText : undefined,
          };
        });
        console.info(`[Server] Set "${setName}": ${normalized.length} Fragen geladen.`);
        return normalized;
      }
    }
  } catch (e) {
    // ignore and fall back to excel
  }

  // Fallback: try excel with several prefixes
  const variants = [setName, setName.replace(/_/g, '-'), setName.replace(/-/g, '_')];
  for (const v of variants) {
    const q = await loadQuestionsFromExcel(v);
    if (q && q.length > 0) return q;
  }

  return [];
}

export async function listSetsFromExcel(): Promise<string[]> {
  try {
    const res = await fetch('/assets/Quiz.xlsx');
    if (!res.ok) return [];
    const ab = await res.arrayBuffer();
    const wb = XLSX.read(ab, { type: 'array' });
    // return sheet names that look like M#_U# or any non-empty
    const names: string[] = Array.isArray(wb.SheetNames) ? wb.SheetNames.slice() : [];
    return names.filter(n => typeof n === 'string' && n.trim().length > 0);
  } catch (e) {
    console.warn('listSetsFromExcel failed', e);
    return [];
  }
}

export const quizService = {
  getQuizList,
  resolveQuizSetForUnit,
  checkSetHasQuestions,
  loadQuestionsFromExcel,
  loadQuestionsForSetName,
  listSetsFromExcel,
};

export default quizService;
